import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import Field from "components/Field";
import SelectProduct from "components/SelectProduct";
import SelectProductVersion from "components/SelectProductVersion";
import SelectProductYear from "components/SelectProductYear";
import SelectSkuAvailability from "components/SelectSkuAvailability";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { pick } from "lodash";
import SelectProductType from "pages/EvaluationsPage/SelectProductType";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  CertificateProductExtended,
  CertificateProductRequest,
  ICertificateProductRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { CommonFormProps, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";

type CertificateProductFormProps = {
  certificateId: string;
  certificateProductId?: string | undefined;
} & CommonFormProps<CertificateProductExtended, ICertificateProductRequest>;

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

const CertificateProductForm = ({
  certificateId,
  certificateProductId,
  entity: certificateSku,
  onCancel,
  onSubmit,
}: CertificateProductFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { certificateService } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const initialValues = useMemo<ICertificateProductRequest>(
    () => ({
      id: certificateProductId,
      ...pick(
        certificateSku ?? [],
        "productId",
        "productTypeId",
        "skuAvailabilityId",
        "productVersionId",
        "productYearId"
      ),
    }),
    [certificateProductId, certificateSku]
  );

  return useMemo(() => {
    const onInnerSubmit = async (values: ICertificateProductRequest) => {
      if (
        values.productId == null &&
        values.productTypeId == null &&
        values.skuAvailabilityId == null &&
        values.productVersionId == null &&
        values.productYearId == null
      ) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "certificateProductEmptyValuesMessage" })
          )
        );
        return null;
      }
      try {
        const response = await registerPromise(
          certificateService.saveCertificateProduct(
            certificateId,
            new CertificateProductRequest(values)
          )
        );

        //parent submit to show message and close dialog
        onSubmit(values);
        return response;
      } catch (error) {
        return { [FORM_ERROR]: translateError(error, t) };
      }
    };

    return (
      <Form
        onSubmit={onInnerSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Typography>
                <T id="certificateProductCreateButtonLabel" />
              </Typography>
              <Grid container spacing={1}>
                <Field name="productId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectProduct
                          {...input}
                          organizationId={organizationId}
                          searchByName={true}
                          label={t({
                            id: "certificateProductProductLabel",
                          })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="productYearId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectProductYear
                          {...input}
                          label={t({
                            id: "certificateProductProductYearLabel",
                          })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="skuAvailabilityId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectSkuAvailability
                          label={t({ id: "skuAvailabilityLabel" })}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="productVersionId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectProductVersion
                          {...input}
                          label={t({
                            id: "certificateProductProductVersionLabel",
                          })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="productTypeId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectProductType
                          {...input}
                          label={t({
                            id: "certificateProductProductTypeLabel",
                          })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              {submitting && <CircularProgress size={24} />}
              <Button
                disabled={submitting}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="saveButton"
              >
                {t({ id: "saveButton" })}
              </Button>
            </DialogActions>
            <SnackbarMessage
              snackbarMessage={snackbarMessage}
              setSnackbarMessage={setSnackbarMessage}
            />
          </form>
        )}
      />
    );
  }, [
    initialValues,
    t,
    registerPromise,
    certificateService,
    certificateId,
    onSubmit,
    styles.feedback,
    onCancel,
    snackbarMessage,
    organizationId,
  ]);
};

export default CertificateProductForm;
